import { NumberField } from '@base-ui/react/number-field';
import { FC, useContext } from 'react';
import { FieldContext } from '../Field/Context';
import InputGroup from '../InputGroup/InputGroup';
import RawInput, { RawInputProps } from './RawInput';

export type NumberInputProps = {
  /**
   * The styled input to render. Defaults to {@link RawInput}; pass
   * {@link InputGroup.RawInput} when composing the input within an `InputGroup`.
   */
  as?: typeof RawInput | typeof InputGroup.RawInput;
  value: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  /**
   * Amount to step by with the arrow keys. Defaults to `'any'`, which permits
   * decimals of any precision and disables step-based validation. Pass a number
   * (e.g. `1`) to constrain entry to multiples of that step.
   */
  step?: number | 'any';
  /**
   * `Intl.NumberFormat` options used to format the displayed value, e.g.
   * `{ maximumFractionDigits: 2 }` to cap decimal places.
   */
  format?: Intl.NumberFormatOptions;
} & Omit<RawInputProps, 'type' | 'value' | 'onChange' | 'step' | 'min' | 'max'>;

const NumberInput: FC<NumberInputProps> = (props) => {
  const {
    as: InputComponent = RawInput,
    value,
    onChange,
    min,
    max,
    step = 'any',
    format,
    disabled,
    inputClassName,
    ...rest
  } = props;

  // Reuse the surrounding Field's id so Base UI labels the input with the same
  // id our <Field.Label htmlFor> targets, rather than generating its own.
  const { id } = useContext(FieldContext);

  return (
    <NumberField.Root
      id={id}
      value={value}
      onValueChange={(newValue) => onChange?.(newValue)}
      min={min}
      max={max}
      step={step}
      format={format}
      disabled={disabled}
      // Render without a wrapping element so the input stays a direct child of
      // any surrounding InputGroup, preserving its sibling-based border radii.
      render={(rootProps) => <>{rootProps.children}</>}
    >
      <NumberField.Input
        render={<InputComponent {...rest} inputClassName={inputClassName} />}
      />
    </NumberField.Root>
  );
};

export default NumberInput;
